import { useEffect, useState } from 'react';
import { staffAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash2, User, Loader2, Mail, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });

  const loadData = async () => {
    try {
      const [staffRes, invitesRes] = await Promise.all([
        staffAPI.getAll(),
        staffAPI.getInvites()
      ]);
      setStaff(staffRes.data);
      setInvites(invitesRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await staffAPI.create(form);
      toast.success(`Invitation envoyee a ${form.email}`);
      setDialogOpen(false);
      setForm({ name: '', email: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce membre du staff ?')) return;
    try {
      await staffAPI.delete(id);
      toast.success('Membre supprime');
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const pendingInvites = invites.filter(i => !i.accepted);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>Staff</h1>
          <p className="text-gray-500">Gerez les membres de votre equipe</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-btn">
              <Plus className="w-4 h-4 mr-2" />Inviter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Inviter un membre du staff</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <Label htmlFor="staff-name">Nom</Label>
                <Input id="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Jean Dupont" data-testid="staff-name-input" />
              </div>
              <div>
                <Label htmlFor="staff-email">Email</Label>
                <Input id="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="jean@email.com" data-testid="staff-email-input" />
              </div>
              <p className="text-xs text-gray-400">Un email sera envoye pour que le membre cree son mot de passe.</p>
              <Button type="submit" className="w-full" disabled={saving} data-testid="save-staff-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Envoyer l'invitation
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Clock className="w-4 h-4 text-amber-500" />
              Invitations en attente ({pendingInvites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingInvites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <Mail className="w-4 h-4 text-amber-500" />
                  <div className="flex-1">
                    <span className="font-medium text-sm">{invite.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{invite.email}</span>
                  </div>
                  <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs">En attente</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Equipe ({staff.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {staff.map((member) => (
              <div key={member.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-testid={`staff-row-${member.id}`}>
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{member.name}</h3>
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
                <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(member.id)} data-testid={`delete-staff-${member.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          {staff.length === 0 && (
            <div className="text-center py-8 text-gray-400">Aucun membre du staff</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
